// flusher — drains the outbox by retrying each entry against the
// SDK's DeviceClient. One entry at a time so a flaky network doesn't
// blast the API with bursts; FIFO so the chronological order at the
// admin dashboard matches the order at the gate.
//
// Outcomes:
//
//   - success or `deduped` 2xx → remove the entry from the outbox.
//   - UnauthorizedError → fire onRevoked and stop (the device's
//     token has been killed; further retries are wasteful and would
//     fill the audit log with 401 noise).
//   - any other error (network, 5xx, 4xx) → leave the entry in
//     place; the next tick retries.
//
// The flusher is *idle-driven*: it ticks on a setInterval but also
// flushes immediately when wake() is called (after a fresh enqueue,
// for the "try now" optimistic path). When already flushing it
// dedupes so concurrent wake() calls are safe.

import { UnauthorizedError, type DeviceClient } from "@sms/sdk";

import type { OutboxAPI, OutboxEntry } from "./outbox";

export interface FlusherOptions {
  outbox: OutboxAPI;
  client: Pick<DeviceClient, "recordArrival">;
  /** Polled in ms. Default 5 s. Tests pass a tiny value or zero. */
  intervalMs?: number;
  /**
   * Predicate for "should I bother trying right now?" — production
   * passes `() => navigator.onLine`; tests override to true|false.
   */
  isOnline?: () => boolean;
  /** Called when the server reports the device is no longer valid. */
  onRevoked?: () => void;
  /** Called after every individual flush attempt (success or fail). */
  onAttempt?: (entry: OutboxEntry, result: FlushResult) => void;
}

export type FlushResult =
  | { kind: "ok"; deduped: boolean; isLate: boolean }
  | { kind: "skip"; reason: "offline" | "empty" | "busy" }
  | { kind: "fail"; error: unknown };

export interface Flusher {
  start(): void;
  stop(): void;
  /** Trigger an immediate flush attempt. Resolves with the first attempt's result. */
  wake(): Promise<FlushResult>;
}

export function createFlusher(opts: FlusherOptions): Flusher {
  const intervalMs = opts.intervalMs ?? 5000;
  const isOnline = opts.isOnline ?? (() => (typeof navigator === "undefined" ? true : navigator.onLine));
  let timer: ReturnType<typeof setInterval> | null = null;
  let flushing = false;

  async function tickOnce(): Promise<FlushResult> {
    if (flushing) return { kind: "skip", reason: "busy" };
    if (!isOnline()) return { kind: "skip", reason: "offline" };
    const entry = opts.outbox.peek();
    if (!entry) return { kind: "skip", reason: "empty" };
    flushing = true;
    try {
      const res = await opts.client.recordArrival(entry.request, {
        idempotencyKey: entry.id,
      });
      opts.outbox.remove(entry.id);
      const out: FlushResult = {
        kind: "ok",
        deduped: res.deduped,
        isLate: res.is_late,
      };
      opts.onAttempt?.(entry, out);
      return out;
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        opts.onRevoked?.();
        // Leave the entry in place; whoever re-pairs the device can
        // decide whether to retry or clear.
      }
      const out: FlushResult = { kind: "fail", error: err };
      opts.onAttempt?.(entry, out);
      return out;
    } finally {
      flushing = false;
    }
  }

  return {
    start() {
      if (timer) return;
      timer = setInterval(() => {
        void tickOnce();
      }, intervalMs);
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = null;
    },
    async wake() {
      return tickOnce();
    },
  };
}
